import { Check, ChevronDown, type LucideIcon } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SelectMenuOption = {
  value: string;
  label: string;
  description?: string;
};

type SelectMenuProps = {
  value: string;
  options: SelectMenuOption[];
  onChange: (value: string) => void;
  label?: string;
  ariaLabel?: string;
  icon?: LucideIcon;
  className?: string;
};

type MenuPosition = { top: number; left: number; width: number };

export function SelectMenu({ value, options, onChange, label, ariaLabel, icon: Icon, className = '' }: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({ top: 0, left: 0, width: 270 });
  const listId = useId();
  const selected = options.find(option => option.value === value) ?? options[0];
  const disabled = options.length === 0;

  useEffect(() => {
    if (!open) return;
    function closeOnOutsidePress(event: PointerEvent) {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    function positionMenu() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.max(rect.width, 270);
      const estimatedHeight = Math.min(options.length * 58 + 16, 340);
      const roomBelow = window.innerHeight - rect.bottom;
      const top = roomBelow >= estimatedHeight || roomBelow >= rect.top
        ? rect.bottom + 8
        : Math.max(8, rect.top - estimatedHeight - 8);
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
      setMenuPosition({ top, left, width });
    }
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      const target = menuRef.current?.querySelector<HTMLButtonElement>('button[aria-selected="true"]')
        ?? menuRef.current?.querySelector<HTMLButtonElement>('button');
      target?.focus();
    });
  }, [open]);

  function choose(next: string) {
    onChange(next);
    setOpen(false);
  }

  return (
    <div className={`custom-select ${open ? 'open' : ''} ${className}`.trim()} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="custom-select-trigger"
        aria-label={ariaLabel || label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => setOpen(current => !current)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {label && <span className="custom-select-label">{label}</span>}
        <span className="custom-select-value">
          {Icon && <Icon size={18} />}
          <span>{selected?.label ?? 'No options available'}</span>
          <ChevronDown className="custom-select-chevron" size={16} />
        </span>
      </button>

      {open && createPortal(
        <div
          className="custom-select-menu custom-select-portal"
          id={listId}
          role="listbox"
          aria-label={ariaLabel || label}
          ref={menuRef}
          style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, width: menuPosition.width, zIndex: 1000 }}
          onKeyDown={event => {
            const buttons = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
            const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const direction = event.key === 'ArrowDown' ? 1 : -1;
              buttons[(current + direction + buttons.length) % buttons.length]?.focus();
            }
            if (event.key === 'Home' || event.key === 'End') {
              event.preventDefault();
              buttons[event.key === 'Home' ? 0 : buttons.length - 1]?.focus();
            }
          }}
        >
          {options.map(option => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? 'selected' : ''}
              key={option.value || 'any'}
              onClick={() => choose(option.value)}
            >
              <span><strong>{option.label}</strong>{option.description && <small>{option.description}</small>}</span>
              {option.value === value && <Check size={17} />}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
